import React, { useEffect, useMemo, useState, useContext, useRef } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { HomeOutlined } from '@mui/icons-material'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import PhoneNumberField from '../../components/common/PhoneNumberField'
import { ReactComponent as Logo } from '../../assets/icons/logo-12.svg'
import { ReactComponent as LogoDark } from '../../assets/icons/logo-12-white.svg'
import { useAuth, useAuthMe } from '../../hooks'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
	createUserProfileScheme,
	CreateUserProfileFormScheme,
	organizationSlugSchema,
} from '../../lib/schemas'
import { useUpdateUserProfile } from '../../hooks/api/userHooks'
import {
	useCreateOrganization,
	useUpdateOrganization,
	useCheckOrganizationSlug,
} from '../../hooks/api/organizationHooks'
import { useGetPrimarySectors } from '../../hooks/api/categoryHooks'
import toastCaller from '../../lib/utils/toastCaller'
import MfaConfirmationModal from '../../pages/Settings/UserPage/components/MfaConfirmationModal'
import Input from '../../components/ui/Input'
import Button from '../../components/ui/Button'
import Checkbox from '../../components/ui/Checkbox'
import Select from '../../components/ui/Select'
import { OrganizationService, UserService } from '../../lib/api'
import { useDebounce } from '../../hooks/common/useDebounce'
import {
	mapFormValuesToUpdateProfile,
	mapFormValuesToOrganizationData,
	inferAccountTypeFromEmail,
	extractDomainFromEmail,
	extractDomainStem,
} from '../../lib/utils/mappers/postRegisterMapper'
import Loader from '../../components/common/Loader'
import { MainLayoutType } from '../../components/layouts/MainLayout'
import AppContext from '../../context/appContext'

dayjs.extend(utc)
dayjs.extend(timezone)

const CURRENT_TIMEZONE = dayjs.tz.guess() || 'America/New_York'

const PostRegisterPage: React.FC = () => {
	const {
		currentUser: currentUserState,
		setCurrentUser,
		isAuthenticated,
	} = useAuth()

	const { data: authMeUser, isPending: isLoadingAuthMe } = useAuthMe({
		enabled: !isAuthenticated,
		retry: false,
	})

	const { darkMode } = useContext(AppContext)
	const currentUser = currentUserState || authMeUser?.data
	const userEmail = currentUser?.profile?.email || ''
	const userProfile = currentUser?.profile

	const { isApp } = useOutletContext<MainLayoutType>()

	const navigate = useNavigate()

	const [phoneError, setPhoneError] = useState<string | false>(false)

	const accountType = useMemo(
		() => (userEmail ? inferAccountTypeFromEmail(userEmail) : 'personal'),
		[userEmail],
	)

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		getValues,
		reset,
		setError,
		clearErrors,
		trigger,
		formState: { errors, isSubmitting, isDirty },
	} = useForm<CreateUserProfileFormScheme>({
		resolver: zodResolver(createUserProfileScheme),
		defaultValues: {
			firstName: userProfile?.firstName || '',
			lastName: userProfile?.lastName || '',
			email: userEmail,
			phone: userProfile?.phone || '',
			organizationName: currentUser?.organization?.name || '',
			organizationSlug: currentUser?.organization?.slug || '',
			primarySector: currentUser?.organization?.primarySector || '',
			enable_mfa: false,
			account_type: accountType,
			is_org_owner: currentUser?.organization?.isOwner,
		},
	})

	const watchedValues = watch()

	const isOrganizationOwner = watchedValues.is_org_owner
	const organizationSlug = watchedValues.organizationSlug || ''
	const primarySector = watchedValues.primarySector || ''
	const debouncedSlug = useDebounce(organizationSlug, 500)
	const currentOrganizationSlug = currentUser?.organization?.slug
		?.trim()
		.toLowerCase()

	const { mutateAsync: updateUserProfile, isPending: isPendingUpdateProfile } =
		useUpdateUserProfile()
	const {
		mutateAsync: createOrganization,
		isPending: isPendingCreateOrganization,
	} = useCreateOrganization()
	const {
		mutateAsync: updateOrganization,
		isPending: isPendingUpdateOrganization,
	} = useUpdateOrganization()
	const {
		mutateAsync: checkSlug,
		isPending: isPendingCheckSlug,
		data: checkSlugResponse,
		variables: checkSlugVariables,
	} = useCheckOrganizationSlug()

	const [isMfaModalOpen, setIsMfaModalOpen] = useState(false)
	const { data: primarySectorsResponse, isLoading: isLoadingPrimarySectors } =
		useGetPrimarySectors()

	const hasInitializedOrgRef = useRef(false)

	useEffect(() => {
		if (
			!isAuthenticated ||
			!userEmail ||
			accountType !== 'organization' ||
			hasInitializedOrgRef.current ||
			isLoadingAuthMe ||
			!isDirty
		)
			return

		const initialize = async () => {
			const domain = extractDomainFromEmail(userEmail)
			const domainStem = extractDomainStem(domain)

			const orgResponse = await OrganizationService.checkOrganizationExist({
				domain,
			})

			const orgData = (orgResponse as any)?.data?.data

			// Only set values if they are empty (not user-entered)
			const currentName = watchedValues.organizationName
			const currentSlug = watchedValues.organizationSlug

			!currentName &&
				setValue(
					'organizationName',
					orgData ? (orgData.name ?? domainStem) : domainStem,
				)

			!currentSlug &&
				setValue('organizationSlug', orgData ? (orgData.slug ?? '') : '')

			hasInitializedOrgRef.current = true
		}

		initialize()
	}, [isAuthenticated, userEmail, accountType])

	useEffect(() => {
		if (currentUser) {
			reset({
				...getValues(),
				firstName: currentUser?.profile?.firstName || '',
				lastName: currentUser?.profile?.lastName || '',
				phone: currentUser?.profile?.phone || '',
				primarySector: currentUser?.organization?.primarySector || '',
				is_org_owner: Boolean(
					accountType === 'organization' && currentUser?.organization === null
						? true
						: currentUser?.organization?.isOwner,
				),
			})
		}
	}, [accountType, currentUser, getValues, reset])

	useEffect(() => {
		const checkOrgSlug = async () => {
			const normalized = debouncedSlug
				?.trim()
				.toLowerCase()
				.replace(/\s+/g, '-')

			if (!normalized || !isAuthenticated || accountType !== 'organization') {
				return
			}

			if (normalized === currentOrganizationSlug) {
				clearErrors('organizationSlug')
				return
			}

			const slugValidation = organizationSlugSchema.safeParse(normalized)
			if (!slugValidation.success) {
				trigger('organizationSlug')
				return
			}

			clearErrors('organizationSlug')

			try {
				const slugResponse = await checkSlug({
					slug: normalized,
				})
				const currentSlug = getValues('organizationSlug')
					?.trim()
					.toLowerCase()
					.replace(/\s+/g, '-')

				if (currentSlug !== normalized) return

				!!slugResponse?.data
					? setError(
							'organizationSlug',
							{
								type: 'manual',
								message: 'This slug is already used',
							},
							{ shouldFocus: false },
						)
					: clearErrors('organizationSlug')
			} catch (error) {
				// Error is handled by the hook
			}
		}

		checkOrgSlug()
	}, [
		accountType,
		checkSlug,
		clearErrors,
		currentOrganizationSlug,
		debouncedSlug,
		getValues,
		isAuthenticated,
		setError,
		trigger,
	])

	const handleSlugChange = (value: string) => {
		const normalized = String(value?.trim())
			?.toLowerCase()
			?.replace(/\s+/g, '-')
		setValue('organizationSlug', normalized, { shouldValidate: true })
		// Don't clear errors here - let Zod validation show errors
	}

	const navigateToHome = async () => {
		const homePath = currentUser?.userFolderId
			? `/home?id=${currentUser?.userFolderId}`
			: '/home'
		navigate(homePath)
	}

	const onSubmit = async (data: CreateUserProfileFormScheme) => {
		if (!isAuthenticated) {
			toastCaller({
				type: 'error',
				message: 'You must be authenticated to submit this form',
			})
			navigate(
				process.env.REACT_APP_LOGOUT_REDIRECT_URI || window.location.origin,
			)
			return
		}

		const handleOrganizationError = (error: any) => {
			const errorData = error?.response?.data || error?.data
			const errorCode = errorData?.errors?.[0] || errorData?.data?.errors?.[0]

			if (errorCode === 'ORGANIZATION_SLUG_ALREADY_EXISTS') {
				setError('organizationSlug', {
					type: 'manual',
					message: 'This slug is already used',
				})
			}
		}

		const handleProfileUpdateSuccess = async () => {
			const userResponse = await UserService.getUser()

			if (userResponse?.data) {
				setCurrentUser(userResponse.data as any)
				data?.enable_mfa ? setIsMfaModalOpen(true) : navigateToHome()
			}
		}

		const updateUserProfileWithCallback = () => {
			updateUserProfile(
				{
					data: mapFormValuesToUpdateProfile({
						formData: data,
						timezone: CURRENT_TIMEZONE,
					}) as any,
				},
				{
					onSuccess: handleProfileUpdateSuccess,
				},
			)
		}

		const handleOrganizationSuccess = () => {
			updateUserProfileWithCallback()
		}

		if (data.account_type === 'organization' && data?.is_org_owner) {
			const normalizedSlug = data.organizationSlug
				?.trim()
				.toLowerCase()
				.replace(/\s+/g, '-')

			if (normalizedSlug && normalizedSlug !== currentOrganizationSlug) {
				const orgCheck = await checkSlug({
					slug: normalizedSlug,
				})

				if (orgCheck?.data === true) {
					setError('organizationSlug', {
						type: 'manual',
						message: 'This slug is already used',
					})
					return
				}
			}

			const orgData = mapFormValuesToOrganizationData({ formData: data })

			const userCheckResponse = await UserService.getUser()

			const userOrganizationId =
				userCheckResponse?.data?.organization?.id ||
				currentUser?.organization?.id

			if (userOrganizationId) {
				await updateOrganization(
					{
						data: orgData,
						id: userOrganizationId,
					},
					{
						onSuccess: handleOrganizationSuccess,
						onError: handleOrganizationError,
					},
				)
			} else {
				await createOrganization(
					{
						data: orgData,
					},
					{
						onSuccess: handleOrganizationSuccess,
						onError: handleOrganizationError,
					},
				)
			}
		} else {
			updateUserProfileWithCallback()
		}
	}

	const handlePhoneError = (error: string | false) => {
		setPhoneError(error)
	}

	const isLoading =
		isSubmitting ||
		isPendingUpdateProfile ||
		isPendingCreateOrganization ||
		isPendingUpdateOrganization

	const normalizedOrganizationSlug = organizationSlug
		.trim()
		.toLowerCase()
		.replace(/\s+/g, '-')
	const isCheckingCurrentSlug =
		isPendingCheckSlug &&
		checkSlugVariables?.slug === normalizedOrganizationSlug
	const slugExists =
		checkSlugResponse?.data === true &&
		checkSlugVariables?.slug === normalizedOrganizationSlug
	const isSubmitDisabled =
		!isDirty ||
		isLoading ||
		(isOrganizationOwner &&
			(slugExists ||
				isCheckingCurrentSlug ||
				!primarySector ||
				isLoadingPrimarySectors))

	return (
		<div className="flex items-center justify-center min-h-[calc(100vh-40px)] z-50 overflow-y-auto top-0 left-0">
			<div className="md:w-[600px] bg-white dark:bg-background-dark rounded-xl py-28 h-fit">
				<div className="flex flex-col items-center justify-center">
					<div className="flex flex-col items-center justify-center">
						{darkMode ? (
							<LogoDark className="w-10 h-10" />
						) : (
							<Logo className="w-10 h-10" />
						)}
						<div className="px-4 flex flex-col items-center justify-start mb-8">
							<h3 className="font-extrabold text-2xl bg-gradient-to-r dark:text-text-main-dark font-roboto">
								AURA
							</h3>
							<p className="font-normal text-[10px] text-text-secondary dark:text-stroke-dark-dark text-nowrap">
								Powered by <span className="font-bold">Solarion AI</span>
							</p>
						</div>
					</div>
				</div>
				<div className="flex items-center justify-center mb-7">
					<p className="text-[22px] text-[#130E09] font-bold align-middle dark:text-stroke-light">
						{isOrganizationOwner
							? 'Create Organization Account'
							: currentUser?.organization
								? 'Create Profile'
								: 'Create Personal Account'}
					</p>
				</div>

				<form
					onSubmit={handleSubmit(onSubmit)}
					className="mx-auto w-[360px] flex flex-col gap-3"
				>
					<Controller
						name="firstName"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								wrapperClassName="w-full"
								inputClassName="h-10"
								labelClassName="font-normal mb-1.5"
								label="First Name"
								placeholder="First Name"
								required
								error={errors.firstName?.message}
							/>
						)}
					/>

					<Controller
						name="lastName"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								wrapperClassName="w-full"
								inputClassName="h-10"
								labelClassName="font-normal mb-1.5"
								label="Last Name"
								placeholder="Last Name"
								required
								error={errors.lastName?.message}
							/>
						)}
					/>

					<Controller
						name="email"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								wrapperClassName="w-full"
								inputClassName="h-10 text-gray-400 hover:cursor-not-allowed"
								labelClassName="font-normal mb-1.5"
								label="Email"
								placeholder="Email"
								disabled
								error={errors.email?.message}
							/>
						)}
					/>

					<Controller
						name="phone"
						control={control}
						render={({ field }) => (
							<div className="w-full">
								<label className="text-text-main font-normal text-base mb-2 flex flex-col gap-1 dark:text-text-main-dark">
									<span className="text-inherit">
										Phone Number <span className="text-accent">*</span>
									</span>
								</label>
								<PhoneNumberField
									value={field.value}
									onChange={field.onChange}
									setError={handlePhoneError}
									showError={!!phoneError || !!errors.phone}
									height={'40px'}
								/>
								{(phoneError || errors.phone) && (
									<p className="text-negative text-sm font-normal break-words w-full line-clamp-2 mt-1">
										{phoneError || errors.phone?.message}
									</p>
								)}
							</div>
						)}
					/>

					{!currentUser?.isSocial && !isApp && (
						<Controller
							name="enable_mfa"
							control={control}
							render={({ field }) => (
								<Checkbox
									checked={field.value}
									onChange={(checked) => field.onChange(checked)}
									label="Enable Two-Factor Authentication"
								/>
							)}
						/>
					)}

					{isOrganizationOwner && (
						<>
							<Controller
								name="organizationName"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										wrapperClassName="w-full"
										inputClassName="h-10"
										labelClassName="font-normal mb-1.5"
										label="Organization Name"
										placeholder="Organisation Name"
										required
										error={errors.organizationName?.message}
									/>
								)}
							/>

							<Controller
								name="organizationSlug"
								control={control}
								render={({ field }) => (
									<div className="w-full flex flex-col gap-1">
										<div className="w-full flex items-end gap-2">
											<Input
												{...field}
												wrapperClassName="w-4/5"
												inputClassName="h-10 w-full"
												labelClassName="font-normal mb-1.5"
												label="Organisation Slug"
												required
												onChange={(e) => {
													handleSlugChange(e.target.value)
												}}
												placeholder="my-org"
												error={errors.organizationSlug?.message}
												rightIcon={
													isPendingCheckSlug ? <Loader size={13} /> : null
												}
											/>
											<p className="text-text-main dark:text-text-main-dark mb-2">
												.solarion.ai
											</p>
										</div>
									</div>
								)}
							/>
						</>
					)}

					{isOrganizationOwner && (
						<Controller
							name="primarySector"
							control={control}
							render={({ field }) => (
								<Select
									tooltip="The main industry or business area of your organization."
									tooltipIconPosition="left"
									label="Primary sector"
									required
									value={field.value}
									onChange={(value) => field.onChange(String(value))}
									options={primarySectorsResponse?.data ?? []}
									placeholder={
										isLoadingPrimarySectors
											? 'Loading sectors...'
											: 'Select a sector'
									}
									disabled={isLoadingPrimarySectors}
									error={errors.primarySector?.message}
									wrapperClassName="w-full"
									className="w-full h-10"
									labelClassName="font-normal mb-1.5 flex-col items-start gap-1"
									errorClassName="text-sm font-normal whitespace-normal"
								/>
							)}
						/>
					)}

					<div className="mt-2 flex flex-col gap-3">
						<Button
							label={currentUser?.organization?.id ? 'Update' : 'Create'}
							loading={isLoading}
							disabled={isSubmitDisabled}
							classNameLabel="text-base"
							className="w-full h-[45px]"
							onClick={(e) => {
								e.preventDefault()
								handleSubmit(onSubmit)(e)
							}}
						/>
						{currentUser?.organization?.id && (
							<Button
								variant="secondary"
								label="Home"
								leftIcon={<HomeOutlined className="h-4 w-4" />}
								className="w-full h-[45px]"
								onClick={(event) => {
									event.preventDefault()
									void navigateToHome()
								}}
							/>
						)}
					</div>
				</form>
			</div>
			<MfaConfirmationModal
				isOpen={isMfaModalOpen}
				cancelLabel="Skip"
				actionLabel="Enable"
				onClose={() => {
					setIsMfaModalOpen(false)
					navigateToHome()
				}}
				onCancel={() => {
					setIsMfaModalOpen(false)
					navigateToHome()
				}}
				title="Enable Two-Factor Authentication"
				description="Protect your account by enabling two-factor authentication. You'll need to enter your password and verify with a code from your authenticator app."
			/>
		</div>
	)
}

export default PostRegisterPage
